// src/pages/legal/PrivacyPolicy.js
import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  Chip,
  Stack
} from '@mui/material';
import {
  Security as SecurityIcon,
  ContactMail as ContactMailIcon,
  DataUsage as DataUsageIcon,
  Lock as LockIcon,
  Timeline as TimelineIcon,
  Policy as PolicyIcon
} from '@mui/icons-material';

// Paleta de colores verde pastel
const Colors = {
  primary: '#7FB069',
  primaryLight: '#A8D5BA',
  primaryDark: '#6B8E5A',
  secondary: '#000000',
  accent: '#90C695',
  background: '#F0F8F0',
  surface: '#FFFFFF',
  text: '#000000',
  textSecondary: '#724CA7'
};

const PrivacyPolicy = () => {
  return (
    <Box sx={{ flexGrow: 1, bgcolor: Colors.background, minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `2px solid ${Colors.primaryLight}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <SecurityIcon sx={{ fontSize: 40, color: Colors.primary, mr: 2 }} />
            <Typography variant="h3" sx={{ color: Colors.text, fontWeight: 'bold' }}>
              Política de Privacidad
            </Typography>
          </Box>
          <Typography variant="h6" color={Colors.textSecondary} paragraph>
            AMPA del Bosc - Protección de Datos Personales
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Fecha de última actualización:</strong> {new Date().toLocaleDateString('es-ES')}
            </Typography>
          </Alert>
        </Paper>

        {/* Información del Responsable */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <ContactMailIcon sx={{ mr: 2 }} />
            1. Responsable del Tratamiento
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            El responsable del tratamiento de los datos personales es:
          </Typography>

          <Box sx={{ bgcolor: Colors.background, p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
              AMPA del Bosc
            </Typography>
            <Typography><strong>Dirección:</strong> [Dirección de la escuela]</Typography>
            <Typography><strong>Email:</strong> ampadelbosc@gmail.com</Typography>
            <Typography><strong>Teléfono:</strong> [Teléfono de contacto]</Typography>
            <Typography><strong>CIF:</strong> [CIF de la AMPA]</Typography>
          </Box>
        </Paper>

        {/* Tipos de Datos */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <DataUsageIcon sx={{ mr: 2 }} />
            2. Datos Personales que Tratamos
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            En el marco de nuestras actividades como AMPA, tratamos las siguientes categorías de datos personales:
          </Typography>

          <List>
            <ListItem>
              <ListItemIcon>
                <Chip label="Datos de Identificación" sx={{ bgcolor: Colors.primaryLight }} />
              </ListItemIcon>
              <ListItemText
                primary="Datos personales básicos"
                secondary="Nombre, apellidos, DNI/NIE, fecha de nacimiento, fotografía"
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <Chip label="Datos de Contacto" sx={{ bgcolor: Colors.accent }} />
              </ListItemIcon>
              <ListItemText
                primary="Información de contacto"
                secondary="Dirección postal, teléfono, email, datos de contacto de emergencia"
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <Chip label="Datos Familiares" sx={{ bgcolor: Colors.primaryDark, color: 'white' }} />
              </ListItemIcon>
              <ListItemText
                primary="Información familiar"
                secondary="Datos de hijos/as, curso académico, tutores legales, autorizaciones"
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <Chip label="Datos Económicos" sx={{ bgcolor: Colors.textSecondary, color: 'white' }} />
              </ListItemIcon>
              <ListItemText
                primary="Información económica"
                secondary="Datos bancarios para domiciliaciones, historial de pagos, facturación"
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <Chip label="Datos de Salud" sx={{ bgcolor: '#FF9800', color: 'white' }} />
              </ListItemIcon>
              <ListItemText
                primary="Información sanitaria"
                secondary="Alergias, intolerancias alimentarias, medicación, datos médicos relevantes"
              />
            </ListItem>
          </List>
        </Paper>

        {/* Finalidades */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <TimelineIcon sx={{ mr: 2 }} />
            3. Finalidades del Tratamiento
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            Sus datos personales son tratados para las siguientes finalidades:
          </Typography>

          <Stack spacing={2}>
            <Paper sx={{ p: 3, bgcolor: Colors.background }}>
              <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
                📚 Gestión de Servicios y Actividades AMPA
              </Typography>
              <Typography variant="body2">
                • Gestión de socios y familias de la AMPA<br/>
                • Organización de eventos, actividades y excursiones<br/>
                • Venta de productos escolares (uniformes, material)<br/>
                • Servicios de apoyo a la comunidad educativa
              </Typography>
            </Paper>

            <Paper sx={{ p: 3, bgcolor: Colors.background }}>
              <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
                💰 Gestión Económica y de Productos
              </Typography>
              <Typography variant="body2">
                • Procesamiento de pedidos y pagos online (PayPal)<br/>
                • Gestión de cuotas de socios de la AMPA<br/>
                • Facturación de productos y servicios<br/>
                • Contabilidad y control de inventario
              </Typography>
            </Paper>

            <Paper sx={{ p: 3, bgcolor: Colors.background }}>
              <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
                📢 Comunicación Digital y Notificaciones
              </Typography>
              <Typography variant="body2">
                • Sistema de notificaciones web y móvil<br/>
                • Información sobre eventos y actividades en calendario<br/>
                • Comunicaciones através del portal web<br/>
                • Asistente virtual (chatbot) para consultas
              </Typography>
            </Paper>

            <Paper sx={{ p: 3, bgcolor: Colors.background }}>
              <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
                🛡️ Seguridad Digital y Control de Acceso
              </Typography>
              <Typography variant="body2">
                • Autenticación segura en plataforma web<br/>
                • Control de acceso por roles (admin/usuario)<br/>
                • Protección de datos personales y familiares<br/>
                • Seguridad en transacciones de pago online
              </Typography>
            </Paper>
          </Stack>
        </Paper>

        {/* Base Legal */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <PolicyIcon sx={{ mr: 2 }} />
            4. Base Legal del Tratamiento
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            El tratamiento de sus datos personales se basa en:
          </Typography>

          <List>
            <ListItem>
              <ListItemIcon>
                <Typography variant="h6" color={Colors.primary}>✓</Typography>
              </ListItemIcon>
              <ListItemText
                primary="Consentimiento del interesado"
                secondary="Para el tratamiento de datos especiales (salud) y comunicaciones comerciales"
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <Typography variant="h6" color={Colors.primary}>✓</Typography>
              </ListItemIcon>
              <ListItemText
                primary="Ejecución de un contrato"
                secondary="Para la prestación de servicios contratados (comedor, extraescolares, etc.)"
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <Typography variant="h6" color={Colors.primary}>✓</Typography>
              </ListItemIcon>
              <ListItemText
                primary="Cumplimiento de obligaciones legales"
                secondary="Para cumplir con obligaciones fiscales, contables y administrativas"
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <Typography variant="h6" color={Colors.primary}>✓</Typography>
              </ListItemIcon>
              <ListItemText
                primary="Interés legítimo"
                secondary="Para la gestión administrativa y organizativa de la AMPA"
              />
            </ListItem>
          </List>
        </Paper>

        {/* Conservación */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <TimelineIcon sx={{ mr: 2 }} />
            5. Conservación de los Datos
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            Los datos personales se conservarán durante:
          </Typography>

          <Box sx={{ bgcolor: Colors.background, p: 3, borderRadius: 2 }}>
            <Typography variant="body1" paragraph>
              • <strong>Datos de servicios activos:</strong> Durante la prestación del servicio y hasta 1 año después de su finalización
            </Typography>
            <Typography variant="body1" paragraph>
              • <strong>Datos económicos:</strong> 6 años desde la última operación (obligación fiscal)
            </Typography>
            <Typography variant="body1" paragraph>
              • <strong>Datos de salud:</strong> Durante la escolarización y hasta 3 años después
            </Typography>
            <Typography variant="body1" paragraph>
              • <strong>Videovigilancia:</strong> Máximo 30 días naturales
            </Typography>
            <Typography variant="body1">
              • <strong>Comunicaciones:</strong> Hasta la revocación del consentimiento
            </Typography>
          </Box>
        </Paper>

        {/* Derechos */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <LockIcon sx={{ mr: 2 }} />
            6. Sus Derechos
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            Como titular de los datos personales, usted tiene derecho a:
          </Typography>

          <Stack spacing={2}>
            <Paper sx={{ p: 2, bgcolor: Colors.background }}>
              <Typography variant="h6" gutterBottom>🔍 Derecho de Acceso</Typography>
              <Typography variant="body2">
                Conocer qué datos tenemos sobre usted y cómo los tratamos
              </Typography>
            </Paper>

            <Paper sx={{ p: 2, bgcolor: Colors.background }}>
              <Typography variant="h6" gutterBottom>✏️ Derecho de Rectificación</Typography>
              <Typography variant="body2">
                Solicitar la corrección de datos inexactos o incompletos
              </Typography>
            </Paper>

            <Paper sx={{ p: 2, bgcolor: Colors.background }}>
              <Typography variant="h6" gutterBottom>🗑️ Derecho de Supresión</Typography>
              <Typography variant="body2">
                Solicitar la eliminación de sus datos cuando ya no sean necesarios
              </Typography>
            </Paper>

            <Paper sx={{ p: 2, bgcolor: Colors.background }}>
              <Typography variant="h6" gutterBottom>⏸️ Derecho de Limitación</Typography>
              <Typography variant="body2">
                Solicitar la limitación del tratamiento en determinadas circunstancias
              </Typography>
            </Paper>

            <Paper sx={{ p: 2, bgcolor: Colors.background }}>
              <Typography variant="h6" gutterBottom>📦 Derecho de Portabilidad</Typography>
              <Typography variant="body2">
                Recibir sus datos en formato estructurado y transmitirlos a otro responsable
              </Typography>
            </Paper>

            <Paper sx={{ p: 2, bgcolor: Colors.background }}>
              <Typography variant="h6" gutterBottom>🚫 Derecho de Oposición</Typography>
              <Typography variant="body2">
                Oponerse al tratamiento de sus datos por motivos relacionados con su situación particular
              </Typography>
            </Paper>
          </Stack>

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>¿Cómo ejercer sus derechos?</strong><br/>
              Puede ejercer sus derechos enviando un email a: <strong>ampadelbosc@gmail.com</strong><br/>
              o por correo postal a la dirección indicada en el punto 1.
            </Typography>
          </Alert>
        </Paper>

        {/* Medidas de Seguridad */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <SecurityIcon sx={{ mr: 2 }} />
            7. Medidas de Seguridad
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            Aplicamos medidas técnicas y organizativas apropiadas para garantizar la seguridad de sus datos:
          </Typography>

          <List>
            <ListItem>
              <ListItemIcon>
                <SecurityIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Cifrado de datos sensibles"
                secondary="Uso de protocolos SSL/TLS para transmisiones y cifrado en base de datos"
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <LockIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Control de acceso"
                secondary="Acceso restringido solo a personal autorizado con credenciales seguras"
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <DataUsageIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Copias de seguridad"
                secondary="Realización periódica de backups seguros y recuperación de datos"
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <TimelineIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Auditorías y monitorización"
                secondary="Registro de accesos y auditorías regulares de seguridad"
              />
            </ListItem>
          </List>
        </Paper>

        {/* Contacto DPO */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <ContactMailIcon sx={{ mr: 2 }} />
            8. Contacto y Reclamaciones
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            Para cualquier consulta relacionada con la protección de datos:
          </Typography>

          <Box sx={{ bgcolor: Colors.background, p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
              📧 Contacto de Protección de Datos
            </Typography>
            <Typography><strong>Email:</strong> ampadelbosc@gmail.com</Typography>
            <Typography><strong>Teléfono:</strong> [Teléfono de contacto]</Typography>
            <Typography><strong>Horario:</strong> Lunes a viernes de 9:00 a 17:00</Typography>
          </Box>

          <Alert severity="warning">
            <Typography variant="body2">
              <strong>Derecho a reclamar:</strong><br/>
              Si considera que el tratamiento de sus datos no se ajusta a la normativa, puede presentar una reclamación ante la Agencia Española de Protección de Datos (www.aepd.es).
            </Typography>
          </Alert>
        </Paper>

        {/* Footer */}
        <Paper sx={{ p: 3, bgcolor: Colors.primaryLight, textAlign: 'center' }}>
          <Typography variant="body2" color={Colors.primaryDark}>
            Esta Política de Privacidad puede ser actualizada periódicamente.<br/>
            Le recomendamos revisar esta página regularmente para estar informado de cualquier cambio.<br/>
            <strong>Última actualización:</strong> {new Date().toLocaleDateString('es-ES')}
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;